import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';
import { uploadStorageFile } from './uploadStorageSlice';

type ResourcePayload = {
  file_type: string;
  file_url: string;
  file_name: string;
  student_id: string | string[];
};

type UploadAndCreatePayload = {
  coachId: string;
  file: File;
  fileType: string;
  fileName?: string;
  student_id: string | string[];
};

type CreateResourcePayload = {
  coachId: string;
  fileType: string;
  fileUrl: string;
  fileName: string;
  student_id: string | string[];
};

type CreateResourceState = {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  data: unknown | null;
};

const initialState: CreateResourceState = {
  loading: false,
  error: false,
  errorMessage: '',
  data: null,
};

export const uploadAndCreateResource = createAsyncThunk<
  { fileUrl: string; response: unknown },
  UploadAndCreatePayload
>('uploadAndCreateResource', async (payload, thunkAPI) => {
  const { coachId, file, fileType, fileName, student_id } = payload;
  if (!coachId) {
    throw new Error('Missing coach ID.');
  }

  const uploadResult = await (thunkAPI.dispatch as any)(
    uploadStorageFile({ file, fileType })
  ).unwrap();

  const uploadedUrl =
    (uploadResult.response as any)?.records?.[0]?.url ||
    uploadResult.fileUrl ||
    '';
  if (!uploadedUrl) {
    throw new Error('Upload failed: missing file URL.');
  }

  const resourcePayload: ResourcePayload = {
    file_type: fileType,
    file_url: uploadedUrl,
    file_name: fileName || file.name || 'Resource',
    student_id,
  };

  const response = await api.post(`/api/coaches/${coachId}/resources`, resourcePayload);
  return {
    fileUrl: uploadedUrl,
    response: response.data,
  };
});

export const createResource = createAsyncThunk<
  { fileUrl: string; response: unknown },
  CreateResourcePayload
>('createResource', async (payload) => {
  const { coachId, fileType, fileUrl, fileName, student_id } = payload;
  if (!coachId) {
    throw new Error('Missing coach ID.');
  }

  const resourcePayload: ResourcePayload = {
    file_type: fileType,
    file_url: fileUrl,
    file_name: fileName,
    student_id,
  };

  const response = await api.post(`/api/coaches/${coachId}/resources`, resourcePayload);
  return {
    fileUrl,
    response: response.data,
  };
});

const createResourceSlice = createSlice({
  name: 'createResource',
  initialState,
  reducers: {
    resetCreateResource: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadAndCreateResource.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(uploadAndCreateResource.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(uploadAndCreateResource.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Resource create failed.';
    });
    builder.addCase(createResource.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(createResource.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(createResource.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Resource create failed.';
    });
  },
});

export const { resetCreateResource } = createResourceSlice.actions;
export default createResourceSlice.reducer;
